(() => {
  'use strict';

  if (window.StookrijkCommerceLoaded) return;
  window.StookrijkCommerceLoaded = true;

  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  function initQuantityInputs(scope = document) {
    qsa('[data-quantity-input]', scope).forEach((wrapper) => {
      if (wrapper.dataset.ready === 'true') return;
      wrapper.dataset.ready = 'true';
      const input = wrapper.querySelector('[data-quantity-field]');
      if (!input) return;

      const adjust = (direction) => {
        const step = Number(input.step) || 1;
        const minimum = input.min === '' ? 0 : Number(input.min);
        const maximum = input.max === '' ? Infinity : Number(input.max);
        const current = Number(input.value) || minimum;
        input.value = String(Math.min(maximum, Math.max(minimum, current + direction * step)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      wrapper.querySelector('[data-quantity-minus]')?.addEventListener('click', () => adjust(-1));
      wrapper.querySelector('[data-quantity-plus]')?.addEventListener('click', () => adjust(1));
    });
  }

  function initAutoSubmit(scope = document) {
    qsa('[data-auto-submit]', scope).forEach((control) => {
      if (control.dataset.ready === 'true') return;
      control.dataset.ready = 'true';
      control.addEventListener('change', () => {
        const form = control.form || control.closest('form');
        if (!form) return;
        const matchingHidden = qsa('input[type="hidden"]', form).find((input) => input.name === control.name);
        if (matchingHidden && matchingHidden !== control) matchingHidden.value = control.value;
        form.requestSubmit ? form.requestSubmit() : form.submit();
      });
    });
  }

  function initFilterDrawers(scope = document) {
    qsa('[data-filter-toggle]', scope).forEach((toggle) => {
      if (toggle.dataset.ready === 'true') return;
      toggle.dataset.ready = 'true';
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;
      const closeButton = panel.querySelector('[data-filter-close]');
      const mobileQuery = window.matchMedia('(max-width: 989px)');
      const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

      const syncAccessibility = () => {
        if (!mobileQuery.matches) {
          panel.removeAttribute('role');
          panel.removeAttribute('aria-modal');
          panel.removeAttribute('aria-label');
          panel.removeAttribute('aria-hidden');
          return;
        }
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        panel.setAttribute('aria-label', 'Filters');
        panel.setAttribute('aria-hidden', panel.classList.contains('is-open') ? 'false' : 'true');
      };

      const open = () => {
        panel.classList.add('is-open');
        document.body.classList.add('has-filter-drawer');
        toggle.setAttribute('aria-expanded', 'true');
        syncAccessibility();
        closeButton?.focus();
      };
      const close = () => {
        panel.classList.remove('is-open');
        document.body.classList.remove('has-filter-drawer');
        toggle.setAttribute('aria-expanded', 'false');
        syncAccessibility();
        toggle.focus();
      };

      syncAccessibility();
      toggle.addEventListener('click', open);
      closeButton?.addEventListener('click', close);
      document.addEventListener('keydown', (event) => {
        if (!panel.classList.contains('is-open')) return;
        if (event.key === 'Escape') {
          close();
          return;
        }
        if (event.key !== 'Tab' || !mobileQuery.matches) return;
        const focusable = qsa(focusableSelector, panel).filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (!panel.classList.contains('is-open')) return;
        if (panel.contains(event.target) || toggle.contains(event.target)) return;
        close();
      });
      mobileQuery.addEventListener?.('change', () => {
        if (!mobileQuery.matches && panel.classList.contains('is-open')) {
          panel.classList.remove('is-open');
          document.body.classList.remove('has-filter-drawer');
          toggle.setAttribute('aria-expanded', 'false');
        }
        syncAccessibility();
      });
    });
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `€ ${(Number(cents || 0) / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  function initProductSections(scope = document) {
    qsa('[data-product-section]', scope).forEach((section) => {
      if (section.dataset.ready === 'true') return;
      section.dataset.ready = 'true';

      const form = section.querySelector('[data-product-form]');
      const variantsScript = section.querySelector('[data-product-variants]');
      if (!form || !variantsScript) return;

      let variants = [];
      try { variants = JSON.parse(variantsScript.textContent); } catch (_error) { return; }

      const idInput = form.querySelector('[data-variant-id]');
      const selects = qsa('[data-option-position]', form);
      const addButton = form.querySelector('[data-add-to-cart]');
      const addText = form.querySelector('[data-add-to-cart-text]');
      const error = form.querySelector('[data-product-error]');
      const stickyButton = section.querySelector('[data-sticky-submit]');
      const stickyText = section.querySelector('[data-sticky-submit-text]');
      const stickyPrice = section.querySelector('[data-sticky-price]');
      const availabilityText = section.querySelector('[data-availability-text]');
      const currency = section.dataset.currency || 'EUR';

      const selectMedia = (mediaId) => {
        if (!mediaId) return;
        qsa('[data-media-id]', section).forEach((media) => {
          const isActive = String(media.dataset.mediaId) === String(mediaId);
          media.hidden = !isActive;
          media.classList.toggle('is-active', isActive);
        });
        qsa('[data-media-thumbnail]', section).forEach((thumb) => {
          const isActive = String(thumb.dataset.mediaThumbnail) === String(mediaId);
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      };

      const updatePrice = (variant) => {
        const wrapper = section.querySelector('[data-price-wrapper]');
        if (!wrapper || !variant) return;
        const current = wrapper.querySelector('[data-price-current]');
        const compare = wrapper.querySelector('[data-price-compare]');
        const saleBadge = wrapper.querySelector('[data-price-sale-badge]');
        const soldOutBadge = wrapper.querySelector('[data-price-sold-out-badge]');
        if (current) current.textContent = formatMoney(variant.price, currency);
        const onSale = Number(variant.compare_at_price) > Number(variant.price);
        if (compare) {
          compare.hidden = !onSale;
          compare.textContent = onSale ? formatMoney(variant.compare_at_price, currency) : '';
        }
        if (saleBadge) saleBadge.hidden = !onSale;
        if (soldOutBadge) soldOutBadge.hidden = Boolean(variant.available);
        wrapper.classList.toggle('commerce-price--sale', onSale);
        wrapper.classList.toggle('commerce-price--sold-out', !variant.available);
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, currency);
      };

      const updateVariant = () => {
        const selectedOptions = selects.map((select) => select.value);
        const variant = variants.find((candidate) => candidate.options.every((option, index) => option === selectedOptions[index]));

        if (!variant) {
          if (idInput) idInput.value = '';
          if (addButton) addButton.disabled = true;
          if (stickyButton) stickyButton.disabled = true;
          if (addText) addText.textContent = 'Combinatie niet beschikbaar';
          if (stickyText) stickyText.textContent = 'Niet beschikbaar';
          if (error) {
            error.textContent = 'Deze combinatie is niet beschikbaar. Kies een andere uitvoering.';
            error.hidden = false;
          }
          return;
        }

        if (error) error.hidden = true;
        if (idInput) {
          idInput.value = variant.id;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (addButton) addButton.disabled = !variant.available;
        if (stickyButton) stickyButton.disabled = !variant.available;
        if (addText) addText.textContent = variant.available ? 'In winkelwagen' : 'Tijdelijk niet beschikbaar';
        if (stickyText) stickyText.textContent = variant.available ? 'In winkelwagen' : 'Niet beschikbaar';
        if (availabilityText) availabilityText.textContent = variant.available ? 'Beschikbaar' : 'Neem contact op';
        updatePrice(variant);

        const mediaId = variant.featured_media?.id || variant.featured_image?.id;
        if (mediaId) selectMedia(mediaId);

        const url = new URL(section.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', `${url.pathname}${url.search}`);

        const quantity = form.querySelector('[data-quantity-field]');
        if (quantity && variant.quantity_rule) {
          quantity.min = variant.quantity_rule.min || 1;
          quantity.step = variant.quantity_rule.increment || 1;
          if (variant.quantity_rule.max) quantity.max = variant.quantity_rule.max;
          else quantity.removeAttribute('max');
          if (Number(quantity.value) < Number(quantity.min)) quantity.value = quantity.min;
        }

        section.dispatchEvent(new CustomEvent('stookrijk:variant-change', {
          bubbles: true,
          detail: { variant }
        }));
      };

      selects.forEach((select) => select.addEventListener('change', updateVariant));
      qsa('[data-media-thumbnail]', section).forEach((thumb) => thumb.addEventListener('click', () => selectMedia(thumb.dataset.mediaThumbnail)));
      stickyButton?.addEventListener('click', () => {
        if (!addButton || addButton.disabled) return;
        form.requestSubmit ? form.requestSubmit(addButton) : addButton.click();
      });

      form.addEventListener('submit', () => {
        if (!addButton) return;
        addButton.setAttribute('aria-busy', 'true');
        if (addText) addText.textContent = 'Wordt toegevoegd…';
      });
    });
  }

  const COMPARE_KEY = 'stookrijk:compare:v2';
  const QUOTE_PRODUCT_HANDLES = new Set(['buitenkeuken-op-maat-samenstellen']);

  function getComparedProducts() {
    try {
      const products = JSON.parse(localStorage.getItem(COMPARE_KEY) || '[]');
      return Array.isArray(products)
        ? products
          .filter((item) => item && item.handle && !QUOTE_PRODUCT_HANDLES.has(item.handle))
          .slice(0, 3)
        : [];
    } catch (_error) {
      return [];
    }
  }

  function saveComparedProducts(products) {
    try { localStorage.setItem(COMPARE_KEY, JSON.stringify(products.slice(0, 3))); } catch (_error) { /* Storage may be blocked. */ }
  }

  function createCell(tag, text) {
    const cell = document.createElement(tag);
    if (text !== undefined) cell.textContent = text || 'Niet vermeld';
    return cell;
  }

  function initCompare(scope = document) {
    const bars = qsa('[data-compare-bar]', scope);
    const toggles = qsa('[data-compare-toggle]', scope);
    if (!bars.length && !toggles.length) return;

    const sync = (announcement = '') => {
      const products = getComparedProducts();
      qsa('[data-compare-toggle]').forEach((toggle) => { toggle.checked = products.some((item) => item.handle === toggle.value); });

      qsa('[data-compare-bar]').forEach((bar) => {
        bar.hidden = products.length === 0;
        const count = bar.querySelector('[data-compare-count]');
        const items = bar.querySelector('[data-compare-items]');
        const status = bar.querySelector('[data-compare-status]');
        if (count) count.textContent = String(products.length);
        if (status) status.textContent = announcement;
        if (items) {
          items.replaceChildren();
          products.forEach((product) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = `${product.title} ×`;
            button.setAttribute('aria-label', `Verwijder ${product.title} uit vergelijking`);
            button.addEventListener('click', () => {
              saveComparedProducts(getComparedProducts().filter((item) => item.handle !== product.handle));
              sync(`${product.title} verwijderd.`);
            });
            items.append(button);
          });
        }
      });
    };

    const renderDialog = (dialog) => {
      const products = getComparedProducts();
      const target = dialog.querySelector('[data-compare-table]');
      if (!target) return;
      const table = document.createElement('table');
      table.className = 'compare-table';
      const head = document.createElement('thead');
      const headerRow = document.createElement('tr');
      headerRow.append(createCell('th', 'Model'));
      products.forEach((product) => {
        const cell = createCell('th');
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = '';
          cell.append(image);
        }
        const link = document.createElement('a');
        link.href = product.url;
        link.textContent = product.title;
        cell.append(link);
        const price = document.createElement('p');
        price.textContent = product.price;
        cell.append(price);
        headerRow.append(cell);
      });
      head.append(headerRow);
      table.append(head);

      const body = document.createElement('tbody');
      [['Maat', 'size'], ['Isolatie', 'insulation']].forEach(([label, key]) => {
        const row = document.createElement('tr');
        row.append(createCell('th', label));
        products.forEach((product) => row.append(createCell('td', product[key])));
        body.append(row);
      });
      table.append(body);
      target.replaceChildren(table);
    };

    toggles.forEach((toggle) => {
      if (toggle.dataset.ready === 'true') return;
      toggle.dataset.ready = 'true';
      toggle.addEventListener('change', () => {
        let products = getComparedProducts();
        if (!toggle.checked) {
          products = products.filter((item) => item.handle !== toggle.value);
          saveComparedProducts(products);
          sync(`${toggle.dataset.productTitle} verwijderd.`);
          return;
        }
        if (products.length >= 3) {
          toggle.checked = false;
          sync('Je kunt maximaal drie modellen tegelijk vergelijken.');
          return;
        }
        products.push({
          handle: toggle.value,
          title: toggle.dataset.productTitle || toggle.value,
          url: toggle.dataset.productUrl || '#',
          image: toggle.dataset.productImage || '',
          price: toggle.dataset.productPrice || '',
          size: toggle.dataset.productSize || '',
          insulation: toggle.dataset.productInsulation || ''
        });
        saveComparedProducts(products);
        sync(`${toggle.dataset.productTitle} toegevoegd. ${products.length} van 3 geselecteerd.`);
      });
    });

    qsa('[data-compare-clear]', scope).forEach((button) => {
      if (button.dataset.ready === 'true') return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => { saveComparedProducts([]); sync('Vergelijking gewist.'); });
    });

    qsa('[data-compare-open]', scope).forEach((button) => {
      if (button.dataset.ready === 'true') return;
      button.dataset.ready = 'true';
      const dialog = button.closest('[data-compare-bar]')?.parentElement?.querySelector('[data-compare-dialog]') || document.querySelector('[data-compare-dialog]');
      button.addEventListener('click', () => {
        if (!dialog || getComparedProducts().length === 0) return;
        renderDialog(dialog);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
      dialog?.querySelector('[data-compare-close]')?.addEventListener('click', () => {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      });
    });

    sync();
  }

  function initRecommendations(scope = document) {
    qsa('product-recommendations[data-url]', scope).forEach((element) => {
      if (element.dataset.ready === 'true') return;
      element.dataset.ready = 'true';
      if (element.innerHTML.trim()) {
        element.dataset.loaded = 'true';
        return;
      }
      const load = async () => {
        if (element.dataset.loaded === 'true') return;
        element.dataset.loaded = 'true';
        try {
          const response = await fetch(element.dataset.url);
          if (!response.ok) return;
          const html = document.createElement('div');
          html.innerHTML = await response.text();
          const incoming = html.querySelector('product-recommendations');
          if (incoming?.innerHTML.trim()) element.innerHTML = incoming.innerHTML;
        } catch (_error) { /* Recommendations are optional. */ }
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          load();
        }, { rootMargin: '300px 0px' });
        observer.observe(element);
      } else load();
    });
  }

  function init(scope = document) {
    initQuantityInputs(scope);
    initAutoSubmit(scope);
    initFilterDrawers(scope);
    initProductSections(scope);
    initCompare(scope);
    initRecommendations(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
